import React from 'react'
import { AnalysisCharts } from '@app/components/charts/AnalysisCharts'
import { Filter } from '@app/components/filter/CustomFilters'
import { useAxios } from '@app/utils/useAxios'
import { BackofficeApiActions } from '@lib/Core/API/BackofficeApiActions'
import { BackofficeAPIConfig } from '@lib/Core/API/BackofficeAPIConfig'
import { BackofficeApiEventResponse } from '@lib/Core/API/BackofficeApiEventResponse'
import { getErrorMessage } from '@lib/Error/getErrorMessage'
import { Alert, AlertGroup, AlertProps, Flex, FlexItem, PageGroup, PageSection, PageSectionVariants, Spinner, Text, TextContent } from '@patternfly/react-core'
import { AxiosResponse } from 'axios'
import { ComparisonFilters } from '@app/components/filter/ComparisonFilters'

interface IAlert {
  text: string
  variant: AlertProps['variant']
  timeout?: number
}

interface ComparisonChartsPageConfig {
  tableName: string
  pageTitle: string
  pageDescription: string
  getFiltersActionName: BackofficeApiActions
  getDataActionName: BackofficeApiActions
}

export interface IFilterSet {
  label: string
  filters: Filter[]
  chartOrderData: Record<string, unknown>[]
}

export interface IChartSet {
  filterSet: IFilterSet
  chartData: Record<string, unknown>[]
}

const ComparisonChartsPage: React.FunctionComponent<ComparisonChartsPageConfig> = (config: ComparisonChartsPageConfig) => {
  const api = useAxios()
  const [alerts, setAlerts] = React.useState<IAlert[]>([])
  const [loadingFilters, setLoadingFilters] = React.useState<boolean>(false)
  const [filterSets, setFilterSets] = React.useState<IFilterSet[]>([])
  const [chartSets, setChartSets] = React.useState<IChartSet[]>([])
  const [loadingCharts, setLoadingCharts] = React.useState<boolean>(false)
  const [chartOrderData, setChartOrderData] = React.useState<Record<string, unknown>[]>([])

  // load filter data
  React.useEffect(() => {
    const abortController = new AbortController()

    return () => {
      abortController.abort()
    }
  }, [])

  React.useEffect(() => {
    setChartSets(chartSets.map((val) => ({ ...val, chartData: [...reorderCharts(chartOrderData, val.chartData)] })))
  }, [chartOrderData])

  const onReloadFilters = async (index?: number, updatedFilterSets?: IFilterSet[]) => {
    try {
      setLoadingCharts(true)

      const responses = await Promise.all(
        (updatedFilterSets || filterSets).map(({ filters }, i) => {
          if (typeof index === 'undefined' || i === index) {
            return api.current?.post(BackofficeAPIConfig.Domains.Insights, {
              action: config.getDataActionName,
              data: { filters, config },
            })
          } else {
            return undefined
          }
        })
      )

      const results = responses.map((response, i) => {
        const filterSet = (updatedFilterSets || filterSets)[i]
        if (typeof index === 'undefined' || i === index) {
          if (response && response.data && response.data.result) {
            console.log('QUERY:', response.data.result.query)
            if (chartOrderData.length) {
              return { filterSet, chartData: [...reorderCharts(chartOrderData, response.data.result.charts)] }
            } else {
              return { filterSet, chartData: response.data.result.charts }
            }
          } else {
            throw new Error(`There was an error with filter set "${filterSet.label}": ${JSON.stringify(response?.data, null, 2)}`)
          }
        } else {
          return chartSets[i]
        }
      })

      setChartSets(results)
      setLoadingCharts(false)
    } catch (error) {
      alert(getErrorMessage(error))
      setLoadingCharts(false)
    }
  }

  // reorder and hide/show charts
  const reorderCharts = (newOrder: Record<string, unknown>[], chartData: Record<string, unknown>[]): Record<string, unknown>[] => {
    const chartDataClone = [...chartData]
    const nameToIndex = new Map(chartDataClone.map((obj, index) => [obj.name, index]))
    const reorderedArray = new Array(chartDataClone.length)
    newOrder.forEach((item, newIndex) => {
      const originalIndex = nameToIndex.get(item.name) as number
      reorderedArray[newIndex] = { ...chartDataClone[originalIndex], ...item }
    })
    return reorderedArray
  }

  const onLoadView = async (loadedView: Record<string, any>) => {
    const updatedFilterSets = [...filterSets, { label: loadedView['viewName'], filters: loadedView['filterData'], chartOrderData: loadedView['chartOrderData'] }]
    setFilterSets(updatedFilterSets)

    await onReloadFilters(updatedFilterSets.length - 1, updatedFilterSets)

    const foundChartOrderData = updatedFilterSets.find((n) => n.chartOrderData.length > 0)
    if (foundChartOrderData?.chartOrderData.length) {
      setChartOrderData(foundChartOrderData?.chartOrderData)
    }
  }

  const onUpdateViews = async (updatedFilterSets: IFilterSet[]) => {
    setFilterSets(updatedFilterSets)

    await onReloadFilters(undefined, updatedFilterSets)

    const foundChartOrderData = updatedFilterSets.find((n) => n.chartOrderData.length > 0)
    if (foundChartOrderData?.chartOrderData.length) {
      setChartOrderData(foundChartOrderData?.chartOrderData)
    }
  }

  return (
    <React.Fragment>
      <PageGroup>
        <PageSection variant={PageSectionVariants.light}>
          <TextContent>
            <Text component="h1">{config.pageTitle}</Text>
            <Text component="p">{config.pageDescription}</Text>
          </TextContent>

          {loadingFilters && <Spinner size="lg" className="pf-u-mt-md" />}
          {!loadingFilters && (
            <ComparisonFilters
              config={config as unknown as Record<string, unknown>}
              filterSets={filterSets}
              chartSets={chartSets}
              onUpdateViews={(updatedFilterSets) => onUpdateViews(updatedFilterSets)}
              onReload={(index?: number) => onReloadFilters(index)}
              onReorderCharts={(chartData) => setChartOrderData(chartData)}
              onLoadView={(loadView) => onLoadView(loadView)}
            ></ComparisonFilters>
          )}

          <AlertGroup isLiveRegion>
            {alerts.map((alert, index) => (
              <Alert key={index} timeout={alert.timeout ? alert.timeout : 0} isInline isPlain variant={alert.variant} title={alert.text} className="pf-u-mt-md" />
            ))}
          </AlertGroup>
        </PageSection>
      </PageGroup>

      <Flex style={{ overflow: 'auto', padding: '1rem' }} flexWrap={{ default: 'nowrap' }}>
        {chartSets.map(({ filterSet, chartData }, i) => (
          <FlexItem width={420} key={'chartset' + i}>
            {loadingCharts && <Spinner />}
            {!loadingCharts && <AnalysisCharts columnsOnly={true} label={filterSet.label} chartData={chartData}></AnalysisCharts>}
          </FlexItem>
        ))}
      </Flex>
    </React.Fragment>
  )
}

export { ComparisonChartsPage, ComparisonChartsPageConfig }
